import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Backdrop,
  Box,
  Checkbox,
  CircularProgress,
  Fade,
  FormControlLabel,
  Grid,
  Icon,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  InputLabel,
  LinearProgress,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import {
  ChevronLeft,
  ChevronRight,
  Delete,
  Equalizer,
  Info,
  OpenInFull,
  PermMedia,
  Settings,
} from '@mui/icons-material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { DropsContext } from 'contexts/DropsContext';
import { decryptURL, CustomPagination, findOnArray } from 'utils/functions';
import { useDialog } from 'components/Modals';
import VirtualDrop from 'components/VirtualDrop';
import Container from 'components/Container';
import InputMask from 'components/InputMask';
import Dropdown from 'components/Dropdown';
import Button from 'components/Button';
import Header from 'components/Header';
import Input from 'components/Input';
import Card from 'components/Card';
import api from 'services/api';
import localeText from 'utils/localeText';
import styles from './styles';

const RenderInputs = ({ inputs = [], control }) => {
  return inputs.map((input) => {
    if (input?.value_type === 'string') {
      return (
        <Grid item xs={12} sm={6} key={input?.id}>
          <Tooltip title={input?.hint} placement="top">
            <Box>
              <Input name={input?.id} control={control} label={input?.name} />
            </Box>
          </Tooltip>
        </Grid>
      );
    }
    if (input?.value_type === 'number') {
      return (
        <Grid item xs={12} sm={6} key={input?.id}>
          <Tooltip title={input?.hint} placement="top">
            <Box>
              <InputMask
                name={input?.id}
                control={control}
                label={input?.name}
              />
            </Box>
          </Tooltip>
        </Grid>
      );
    }
    if (input?.value_type === 'list' || input?.value_type === 'boolean') {
      return (
        <Grid item xs={12} sm={6} key={input?.id}>
          <Tooltip title={input?.hint} placement="top">
            <Box>
              <Dropdown
                name={input?.id}
                control={control}
                label={input?.name}
                options={input?.values?.map((v) => ({
                  value: v?.name,
                  label: v?.name,
                }))}
              />
            </Box>
          </Tooltip>
        </Grid>
      );
    }
    return null;
  });
};

const Modal = ({ anuncio, cols, onSubmit }) => {
  const imagens = anuncio?.imagens || [];
  const [selected, setSelected] = useState(null);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6" fontWeight="bold" align="center">
          Selecione uma imagem para adicionar ao anúncio
        </Typography>
      </Grid>
      <Grid item xs={12} display={imagens?.length > 0 ? 'block' : 'none'}>
        <ImageList cols={cols} gap={10} rowHeight={200}>
          {imagens.map((img, index) => (
            <ImageListItem key={index}>
              <Box
                src={img.url}
                loading="lazy"
                component="img"
                alt={`img-${index}`}
                sx={styles?.image}
              />
              <ImageListItemBar
                actionIcon={
                  <>
                    <Checkbox
                      size="small"
                      checked={selected === img?.url}
                      sx={styles?.checkbox}
                      onChange={() => {
                        if (selected === img?.url) {
                          setSelected(null);
                        } else {
                          setSelected(img?.url);
                        }
                      }}
                    />
                    <IconButton
                      size="small"
                      sx={styles?.icon}
                      onClick={() => window.open(img?.url)}
                    >
                      <OpenInFull fontSize="small" />
                    </IconButton>
                  </>
                }
              />
            </ImageListItem>
          ))}
        </ImageList>
      </Grid>
      <Grid item xs={12} textAlign="center">
        <Button
          color="primary"
          variant="contained"
          onClick={() => onSubmit(selected)}
        >
          Adicionar
        </Button>
      </Grid>
    </Grid>
  );
};

const VariacaoModal = ({ anuncio, drops, onSubmit }) => {
  const defaultValues = {
    produto_id: null,
    reserva: 'SIM',
    price: '',
    available_quantity: '',
  };
  const { control, getValues, watch, setValue } = useForm({ defaultValues });
  const atForm = useForm();
  const atcbForm = useForm();
  const reserva = watch('reserva');

  const onConfirm = () => {
    const { produto_id, price, available_quantity, reserva } = getValues();
    const _attributes = atForm?.getValues() || {};
    const _attribute_combinations = atcbForm?.getValues() || {};
    const attributes = Object.keys(_attributes)
      .map((id) => ({
        id,
        value_name: _attributes[id],
      }))
      ?.filter((f) => Boolean(f?.value_name));
    const attribute_combinations = Object.keys(_attribute_combinations)
      .map((id) => ({
        id,
        value_name: _attribute_combinations[id],
      }))
      ?.filter((f) => Boolean(f?.value_name));

    const mainAttributes = anuncio?.attributes?.map((m) => {
      const finder = anuncio?.allAttributes?.find((a) => a?.id === m?.id);
      if (
        finder &&
        (finder?.tags?.allow_variations || finder?.tags?.variation_attribute)
      ) {
        return { id: m?.id, value_name: null };
      }
      return m;
    });

    const data = {
      id: anuncio?.id,
      attributes: mainAttributes,
      variations: [
        ...anuncio?.variations,
        {
          price,
          reserva,
          attributes,
          produto_id,
          available_quantity,
          attribute_combinations,
        },
      ],
    };

    onSubmit(data);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <VirtualDrop
          name="produto_id"
          control={control}
          label="Produto"
          options={drops?.Produto?.filter((f) =>
            [
              '00-Mercadoria para revenda',
              '04-Produto acabado',
              '05-Subproduto',
              '06-Produto intermediário',
            ]?.includes(f?.tipolinha)
          )}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <InputMask name="price" control={control} label="Preço" />
      </Grid>
      <Grid item xs={12} sm={4}>
        <InputMask
          label="Saldo"
          control={control}
          name="available_quantity"
          disabled={reserva === 'NAO'}
        />
      </Grid>
      <Grid
        item
        xs={12}
        sm={4}
        display="flex"
        alignItems="center"
        justifyContent="space-evenly"
      >
        <InputLabel variant="body2">Sincronizar Saldo</InputLabel>
        <Switch
          checked={reserva === 'NAO'}
          onChange={(_, v) => setValue('reserva', v ? 'NAO' : 'SIM')}
        />
      </Grid>
      <RenderInputs
        inputs={anuncio?.allow_variations}
        control={atcbForm?.control}
      />
      <RenderInputs
        inputs={anuncio?.variation_attribute}
        control={atForm?.control}
      />
      <Grid item xs={12} textAlign="center">
        <Button variant="contained" color="primary" onClick={onConfirm}>
          Salvar
        </Button>
      </Grid>
    </Grid>
  );
};

const EditModal = ({ item, anuncio, drops, onSubmit }) => {
  const defaultValues = {
    produto_id: item?.produto_id || null,
  };
  const { control, handleSubmit } = useForm({ defaultValues });

  const onConfirm = (values) => {
    const data = {
      id: anuncio?.id,
      variations: [{ id: item?.id, produto_id: values?.produto_id }],
    };

    onSubmit(data);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <VirtualDrop
          name="produto_id"
          control={control}
          label="Produto"
          options={drops?.Produto?.filter((f) =>
            [
              '00-Mercadoria para revenda',
              '04-Produto acabado',
              '05-Subproduto',
              '06-Produto intermediário',
            ]?.includes(f?.tipolinha)
          )}
        />
      </Grid>
      <Grid item xs={12} textAlign="center">
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit(onConfirm)}
        >
          Salvar
        </Button>
      </Grid>
    </Grid>
  );
};

const Anuncio = ({ anuncio, drops, loading, onSubmit }) => {
  const defaultValues = {
    id: anuncio?.id,
    title: anuncio?.title || '',
    produto_id: anuncio?.produto_id || null,
    price: anuncio?.price || '',
    available_quantity: anuncio?.available_quantity || '',
    saldo_produto: anuncio?.saldo_produto || '',
    reserva: anuncio?.reserva || 'SIM',
    description: anuncio?.description?.plain_text || '',
    local_pick_up: anuncio?.shipping?.local_pick_up || false,
    free_shipping: anuncio?.shipping?.free_shipping || false,
  };
  const { control, handleSubmit, watch, resetField } = useForm({
    defaultValues,
  });
  const reserva = watch('reserva');

  const onConfirm = ({
    id,
    title,
    produto_id,
    reserva,
    price,
    available_quantity,
    description,
    free_shipping,
    local_pick_up,
  }) => {
    let data = { id, produto_id, reserva };
    if (title !== anuncio?.title) {
      data = { ...data, title };
    }
    if (price !== anuncio?.price) {
      data = { ...data, price };
    }
    if (available_quantity !== anuncio?.available_quantity) {
      data = { ...data, available_quantity };
    }
    if (description !== anuncio?.description?.plain_text) {
      data = { ...data, description };
    }
    if (free_shipping !== anuncio?.shipping?.free_shipping) {
      data = { ...data, shipping: { ...data?.shipping, free_shipping } };
    }
    if (local_pick_up !== anuncio?.shipping?.local_pick_up) {
      data = { ...data, shipping: { ...data?.shipping, local_pick_up } };
    }
    onSubmit(data);
  };

  return (
    <Card title="Informações Gerais">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Input name="title" control={control} label="Título" />
        </Grid>
        <Grid item xs={12}>
          <VirtualDrop
            name="produto_id"
            control={control}
            label="Produto"
            options={drops?.Produto?.filter((f) =>
              [
                '00-Mercadoria para revenda',
                '04-Produto acabado',
                '05-Subproduto',
                '06-Produto intermediário',
              ]?.includes(f?.tipolinha)
            )}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <InputMask name="price" control={control} label="Preço" />
        </Grid>
        <Grid item xs={12} sm={3}>
          <InputMask
            name="available_quantity"
            control={control}
            label="Saldo do anúncio"
            disabled={reserva === 'NAO'}
          />
        </Grid>
        <Grid item xs={12} sm={3} display="flex" alignItems="center">
          <Typography>Saldo do produto: {anuncio?.saldo_produto}</Typography>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Controller
            name="reserva"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                label="Sincronizar Saldo"
                control={
                  <Switch
                    checked={field.value === 'NAO'}
                    onChange={(_, checked) => {
                      field.onChange(checked ? 'NAO' : 'SIM');
                      resetField('available_quantity');
                    }}
                  />
                }
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Input
            name="description"
            control={control}
            label="Descrição"
            rows={10}
            multiline
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="local_pick_up"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...field}
                    checked={!!field.value}
                    color="primary"
                  />
                }
                label="Retirada em Mãos"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="free_shipping"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...field}
                    checked={!!field.value}
                    color="primary"
                  />
                }
                label="Frete Grátis"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} textAlign="center">
          <Button
            color="primary"
            loading={loading}
            variant="contained"
            onClick={handleSubmit(onConfirm)}
          >
            Salvar
          </Button>
        </Grid>
      </Grid>
    </Card>
  );
};

const Detalhes = ({ anuncio, loading, onSubmit }) => {
  const renderValues = () => {
    let defaultValues = {};
    anuncio?.allAttributes?.map((a) => {
      defaultValues[a.id] =
        a?.value_type === 'list' || a?.value_type === 'boolean' ? null : '';
    });
    anuncio?.attributes?.map((a) => {
      defaultValues[a.id] =
        a?.value_type === 'list' || a?.value_type === 'boolean'
          ? a?.value_name
          : a?.value_name;
    });
    return defaultValues;
  };
  const { control, handleSubmit } = useForm({ defaultValues: renderValues() });

  const onConfirm = (values) => {
    let data = {
      id: anuncio?.id,
      attributes: Object.keys(values).map((id) => ({
        id,
        value_name: values[id],
      })),
    };
    onSubmit(data);
  };

  return (
    <Card title="Informações Adicionais">
      <Grid container spacing={2}>
        <RenderInputs inputs={anuncio?.allAttributes} control={control} />
        <Grid item xs={12} textAlign="center">
          <Button
            color="primary"
            loading={loading}
            variant="contained"
            onClick={handleSubmit(onConfirm)}
          >
            Salvar
          </Button>
        </Grid>
      </Grid>
    </Card>
  );
};

const Imagens = ({ anuncio, loading, onSubmit }) => {
  const { openDialog } = useDialog();
  const theme = useTheme();
  const xs = useMediaQuery(theme.breakpoints.only('xs'));
  const sm = useMediaQuery(theme.breakpoints.only('sm'));
  const md = useMediaQuery(theme.breakpoints.only('md'));
  const lg = useMediaQuery(theme.breakpoints.only('lg'));
  const imagens = anuncio?.pictures || [];

  const getCols = () => {
    if (xs) return 1;
    if (sm) return 2;
    if (md) return 3;
    if (lg) return 4;
    return 5;
  };

  const cols = getCols();

  const onAddMidia = (source) => {
    if (Boolean(source)) {
      const pictures = anuncio?.pictures?.map((p) => ({
        id: p?.id,
        url: p?.url,
      }));
      pictures.push({ source });
      onSubmit({ id: anuncio?.id, pictures });
    }
  };

  const onDeleteMidia = (id) => {
    const pictures = anuncio?.pictures
      ?.filter((f) => f?.id !== id)
      ?.map((p) => ({ id: p?.id, url: p?.url }));
    onSubmit({ id: anuncio?.id, pictures });
  };

  const onChangePosition = (index, move) => {
    const pictures = anuncio?.pictures?.map((p) => ({
      id: p?.id,
      url: p?.url,
    }));
    const element = pictures[index];
    pictures.splice(index, 1);
    pictures.splice(index + move, 0, element);
    onSubmit({ id: anuncio?.id, pictures });
  };

  return (
    <Card title="Imagens">
      <Grid container spacing={2}>
        <Grid item xs={12} textAlign="center">
          <Button
            color="primary"
            loading={loading}
            variant="outlined"
            onClick={() =>
              openDialog(
                <Modal anuncio={anuncio} cols={cols} onSubmit={onAddMidia} />,
                'Adicionar Imagem'
              )
            }
          >
            Adicionar
          </Button>
        </Grid>
        <Grid item xs={12} display={imagens?.length > 0 ? 'block' : 'none'}>
          <ImageList cols={cols} gap={10} rowHeight={200}>
            {imagens.map((img, index) => (
              <ImageListItem key={index}>
                <Box
                  src={img.url}
                  loading="lazy"
                  component="img"
                  alt={`img-${index}`}
                  sx={styles?.image}
                />
                <ImageListItemBar
                  actionIcon={
                    <>
                      <IconButton
                        size="small"
                        sx={styles?.icon}
                        onClick={() => window.open(img?.url)}
                      >
                        <OpenInFull fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        sx={styles?.icon}
                        disabled={loading}
                        onClick={() => onDeleteMidia(img?.id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        sx={styles?.icon}
                        disabled={index <= 0}
                        onClick={() => onChangePosition(index, -1)}
                      >
                        <ChevronLeft fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={index + 1 >= imagens?.length}
                        sx={styles?.icon}
                        onClick={() => onChangePosition(index, 1)}
                      >
                        <ChevronRight fontSize="small" />
                      </IconButton>
                    </>
                  }
                />
              </ImageListItem>
            ))}
          </ImageList>
        </Grid>
      </Grid>
    </Card>
  );
};

const Variacoes = ({ anuncio, drops, loading, onSubmit }) => {
  const { openDialog } = useDialog();

  return (
    <Card title="Variações">
      <Grid container spacing={2}>
        <Grid item xs={12} textAlign="center">
          <Button
            color="primary"
            loading={loading}
            variant="outlined"
            onClick={() =>
              openDialog(
                <VariacaoModal
                  drops={drops}
                  anuncio={anuncio}
                  onSubmit={onSubmit}
                />,
                'Adicionar Variação'
              )
            }
          >
            Adicionar
          </Button>
        </Grid>
        <Grid item xs={12}>
          <DataGrid
            rows={anuncio?.variations || []}
            columns={[
              {
                field: 'produto_id',
                headerName: 'Produto',
                minWidth: 300,
                flex: 1,
                sortable: false,
                valueGetter: ({ row }) =>
                  findOnArray(row?.produto_id, drops?.Produto, 'label'),
              },
              {
                field: 'variations',
                headerName: 'Variações',
                width: 300,
                sortable: false,
                renderCell: ({ row }) => (
                  <>
                    {(row?.attribute_combinations || []).map((item, index) => (
                      <Box
                        gap={2}
                        key={index}
                        display="flex"
                        alignItems="center"
                      >
                        <Typography variant="caption" fontWeight="bold">
                          {item.name}:
                        </Typography>
                        <Typography variant="caption">
                          {item.value_name}
                        </Typography>
                      </Box>
                    ))}
                  </>
                ),
              },
              {
                field: 'price',
                headerName: 'Preço',
                width: 200,
                sortable: false,
                type: 'number',
                valueGetter: ({ value }) =>
                  value &&
                  value?.toLocaleString('pt-br', {
                    style: 'currency',
                    currency: 'BRL',
                  }),
              },
              {
                field: 'available_quantity',
                headerName: 'Saldo',
                width: 200,
                type: 'number',
                sortable: false,
              },
              {
                field: 'actions',
                headerName: 'Ações',
                type: 'actions',
                width: 100,
                getActions: ({ row }) => [
                  <GridActionsCellItem
                    icon={<Icon>edit</Icon>}
                    label="Editar"
                    onClick={() =>
                      openDialog(
                        <EditModal
                          item={row}
                          drops={drops}
                          anuncio={anuncio}
                          onSubmit={onSubmit}
                        />,
                        'Editar Variação'
                      )
                    }
                  />,
                  <GridActionsCellItem
                    icon={<Icon>delete</Icon>}
                    label="Excluir"
                    onClick={() => {
                      const data = {
                        id: anuncio?.id,
                        variations: (anuncio?.variations || [])?.filter(
                          (f) => f?.id !== row?.id
                        ),
                      };
                      if ((anuncio?.variations || [])?.length === 1) {
                        const attributes = [...(anuncio.attributes || [])];
                        [
                          ...(row?.attributes || []),
                          ...(row?.attribute_combinations || []),
                        ]?.map((m) => {
                          const finder = anuncio?.allAttributes?.find(
                            (a) => a?.id === m?.id
                          );
                          if (
                            finder &&
                            (finder?.tags?.allow_variations ||
                              finder?.tags?.variation_attribute)
                          ) {
                            attributes?.push({
                              id: m?.id,
                              value_name: m?.value_name,
                            });
                          }
                        });
                        data.attributes = attributes;
                      }

                      onSubmit(data);
                    }}
                  />,
                ],
              },
            ]}
            autoHeight
            pagination
            pageSize={10}
            density="compact"
            loading={loading}
            disableColumnMenu
            showCellRightBorder
            showColumnRightBorder
            disableSelectionOnClick
            localeText={localeText}
            getRowHeight={() => 'auto'}
            components={{
              Pagination: CustomPagination,
              LoadingOverlay: LinearProgress,
            }}
          />
        </Grid>
      </Grid>
    </Card>
  );
};

const MercadoLivre = () => {
  const { hash } = useParams();
  const decrypt = decryptURL(hash);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [postLoading, setPostLoading] = useState(false);
  const [anuncio, setAnuncio] = useState(null);
  const { drops } = useContext(DropsContext);
  const { closeDialog } = useDialog();

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {
        id: decrypt?.id,
        integra_access_id: decrypt?.integra_access_id,
      };
      const { data } = await api.get(`/Integracoes/MercadoLivre/Anuncio`, {
        params,
      });
      setAnuncio(data);
    } catch (error) {
    } finally {
      setLoading(false);
      closeDialog();
    }
  };

  const onSubmit = async (data) => {
    try {
      setPostLoading(true);
      await api.put(`/Integracoes/MercadoLivre/Anuncio`, data);
      loadData();
    } catch (error) {
    } finally {
      setPostLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const tabs = [
    {
      value: 0,
      key: 'Anúncio',
      label: 'Anúncio',
      icon: <Settings />,
    },
    {
      value: 1,
      key: 'Detalhes',
      label: 'Detalhes',
      icon: <Info />,
    },
    {
      value: 2,
      key: 'Imagens',
      label: 'Imagens',
      icon: <PermMedia />,
    },
    {
      value: 3,
      key: 'Variações',
      label: 'Variações',
      icon: <Equalizer />,
    },
  ];

  const RenderTabs = () => {
    if (loading) return null;

    const tabProps = { anuncio, onSubmit, drops, loading: postLoading };

    switch (tab) {
      case 0:
        return <Anuncio {...tabProps} />;
      case 1:
        return <Detalhes {...tabProps} />;
      case 2:
        return <Imagens {...tabProps} />;
      case 3:
        return <Variacoes {...tabProps} />;
      default:
        return null;
    }
  };

  return (
    <Container>
      <Header titulo="Editar Anúncio" />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card>
            <Tabs
              variant="fullWidth"
              value={tab}
              onChange={(_, v) => setTab(v)}
              TabIndicatorProps={styles?.tabProps}
              sx={styles?.tab}
            >
              {tabs.map(({ show = true, ...t }) => show && <Tab {...t} />)}
            </Tabs>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <RenderTabs />
        </Grid>
      </Grid>
      <Fade in={loading}>
        <Backdrop open={loading} sx={styles?.loader}>
          <CircularProgress color="inherit" size={48} thickness={4} />
        </Backdrop>
      </Fade>
    </Container>
  );
};

export default MercadoLivre;
